/**
 * Book editor using Custom editor (RTL/LTR optimized)
 */

import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Modal,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { findEnclosingChapterSectionHeadingBlockId } from '@shared/ast/extractChapterSection'
import { headingSignature } from '@shared/ast/headingSignature'
import { stripChapterSectionHeadingPrefix } from '@shared/ast/tocChapterSectionHeading'
import { syncWritingReadiness } from '@shared/ast/writingReadiness'
import type { DependencyKind } from '@shared/chapterSectionDependencies'
import { chapterSectionKey } from '@shared/chapterSectionsOrder'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

import BookEditorHeaderActions from './book-editor/BookEditorHeaderActions'
import { readEditorLinkTarget } from './book-editor/editorLinks'
import { useContentProposals } from './book-editor/hooks/useContentProposals'
import type { InlineProposalReview } from '../components/editor/lexical/InlineProposal'
import { updateContentProposal } from '../services/contentProposals'
import ContentProposalBulkActions from './book-editor/components/ContentProposalBulkActions'
import EditorTabBar from './book-editor/components/EditorTabBar'
import HistoryDialog from './book-editor/components/history/HistoryDialog'
import KeyboardShortcutsDialog from './book-editor/components/KeyboardShortcutsDialog'
import TabSwitcher from './book-editor/components/TabSwitcher'
import EditorDocumentPane from './book-editor/EditorDocumentPane'
import EditorSidebar from './book-editor/EditorSidebar'
import BookSettingsModal from '../components/books/BookSettingsModal'
import DecryptionModal from '../components/decryption/DecryptionModal'
import { LexicalEditor, LexicalEditorRef } from '../components/editor/lexical'
import EncryptedContextMenu from '../components/encryption/EncryptedContextMenu'
import LockPhraseDialog from '../components/encryption/LockPhraseDialog'
import ReleasePassageDialog from '../components/encryption/ReleasePassageDialog'
import Header from '../components/layout/Header'
import { PublishTarget, PublishingModal } from '../components/publishing/PublishingModal'
import { SearchPaneHandle } from '../components/search/SearchPane'
import SearchPreviewPanel from '../components/search/SearchPreviewPanel'
import SidePane, { SidePaneHandle } from '../components/sidepane/SidePane'
import TagManagerDialog from '../components/tags/TagManagerDialog'
import { getTagDisplayName, resolveTagColor } from '../components/tags/tagTreeUtils'
import { useAuth } from '../contexts/AuthContext'
import { useBodyScrollLock } from '../hooks/useBodyScrollLock'
import { Book, ContentFile, apiService } from '../services/api'
import { fetchAssistantAvailability } from '../services/assistantStream'
import { PAPER_BACKGROUND, PAPER_TEXTURE_OVERLAY } from '../styles/paperBackground'
import { AstSelection, ContentAST, HeadingBlock, createEmptyAST, getBlockText } from '../transforms/ast'
import { Annotation } from '../types/annotations'
// compareAST, formatDiffSummary, logASTMismatch kept for future debugging but not called on every save
// import { compareAST, formatDiffSummary, logASTMismatch } from '../utils/astValidation'
import { logger } from '../utils/logger'
import AssistantPane from './book-editor/components/AssistantPane'
import ChapterSectionsToc from './book-editor/components/ChapterSectionsToc'
import ContinuousPlacedSectionsView from './book-editor/components/ContinuousPlacedSectionsView'
import AddDependencyDialog from './book-editor/components/dependencies/AddDependencyDialog'
import { FlaggedSpotsDialog } from './book-editor/components/FlaggedSpotsDialog'
import { LineNumberGutter } from './book-editor/components/LineNumberGutter'
import MobileSearchDialog from './book-editor/components/MobileSearchDialog'
import SortableChapterItem from './book-editor/components/SortableChapterItem'
import { useBookSearchController } from './book-editor/hooks/useBookSearchController'
import { useChapterContentCache } from './book-editor/hooks/useChapterContentCache'
import { useChapterNavigation } from './book-editor/hooks/useChapterNavigation'
import { useChapterPersistence } from './book-editor/hooks/useChapterPersistence'
import { type ChapterSectionEntry, useChapterSectionsBook } from './book-editor/hooks/useChapterSectionsBook'
import { useHistoryAvailability } from './book-editor/hooks/useContentHistory'
import { useEditorTabs } from './book-editor/hooks/useEditorTabs'
import { useEncryptedSectionActions } from './book-editor/hooks/useEncryptedSectionActions'
import { useFlaggedSpotsPrompt } from './book-editor/hooks/useFlaggedSpotsPrompt'
import { usePublishingActions } from './book-editor/hooks/usePublishingActions'
import { useSectionDependencies } from './book-editor/hooks/useSectionDependencies'
import { useTagRegistryState } from './book-editor/hooks/useTagRegistryState'
import { useUndoHistoryCache } from './book-editor/hooks/useUndoHistoryCache'
import { useActiveHeadingTracking } from './book-editor/useActiveHeadingTracking'

const BookEditor: React.FC = () => {
  const { bookId, chapterId } = useParams<{ bookId: string; chapterId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, isLoading: authLoading } = useAuth()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  // Pin the header/toolbar by making the inner content the only scrollable surface (see hook).
  useBodyScrollLock()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  // Detect if we're in public reading mode

  // Per-file content cache so switching back to an already-visited file is instant.
  const contentCache = useChapterContentCache(true)
  // Per-file undo history, so switching back to a tab brings its ⌘Z stack with it.
  const undoHistoryCache = useUndoHistoryCache(true)

  const [loading, setLoading] = useState(true)
  const [_originalContent, setOriginalContent] = useState<ContentAST | null>(null)
  const [currentContent, setCurrentContent] = useState<ContentAST | null>(null)
  // Bumped to force the main LexicalEditor to remount with fresh content after an
  // out-of-band save (e.g. the OrganizeChapters section preview editor saves the same file).
  const [externalContentEpoch, setExternalContentEpoch] = useState(0)
  const [book, setBook] = useState<Book | null>(null)
  const [chapters, setChapters] = useState<ContentFile[]>([])
  const [currentChapter, setCurrentChapter] = useState<ContentFile | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [readOnly, setReadOnly] = useState(false)
  const [debugMode, setDebugMode] = useState(false)
  // Line-number gutter visibility — off by default, choice persisted per book.
  const lineNumbersStorageKey = bookId ? `editor-line-numbers:${bookId}` : null
  const [showLineNumbers, setShowLineNumbers] = useState<boolean>(
    () => !!lineNumbersStorageKey && localStorage.getItem(lineNumbersStorageKey) === 'true',
  )
  const [publishOpen, setPublishOpen] = useState(false)
  const [publishTarget, setPublishTarget] = useState<PublishTarget | null>(null)
  const [bookSettingsOpen, setBookSettingsOpen] = useState(false)
  const [tagManagerOpen, setTagManagerOpen] = useState(false)
  const [tagTargetChapter, setTagTargetChapter] = useState<ContentFile | null>(null)
  const [mobileSidePaneOpen, setMobileSidePaneOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [assistant, setAssistant] = useState<{
    open: boolean
    markedText?: string
    startBlockId?: string
  }>({ open: false })

  const [addDepDialog, setAddDepDialog] = useState<{
    initialFromKey: string
    initialToKey: string
    initialReason: string
    initialKind?: DependencyKind
  } | null>(null)
  const sidePaneRef = useRef<SidePaneHandle>(null)
  const mobileSearchRef = useRef<SearchPaneHandle>(null)
  const [suppressSidebarAutoScroll, setSuppressSidebarAutoScroll] = useState(false)
  const suppressSidebarAutoScrollTimeoutRef = useRef<number | null>(null)
  const bookRef = useRef<Book | null>(null)
  const chaptersRef = useRef<ContentFile[]>([])

  // editor ref for programmatic navigation
  const editorRef = useRef<LexicalEditorRef>(null)
  const searchPreviewEditorRef = useRef<LexicalEditorRef>(null)
  const getEditor = useCallback(() => editorRef.current?.getEditor() ?? null, [])

  const {
    decryptionModalOpen,
    encryptedContentToDecrypt,
    mnemonicMap,
    contextMenuPosition,
    contextMenuEncryptionId,
    contextMenuKind,
    setDecryptionModalOpen,
    setContextMenuPosition,
    handleEncryptedClick,
    handleDecryptSuccess,
    handleEncryptedContextMenu,
    handleRemoveEncryption,
  } = useEncryptedSectionActions(getEditor)
  /** Encryption id the author is about to open to everyone. Confirmed first: irreversible. */
  const [releaseTarget, setReleaseTarget] = useState<string | null>(null)

  /** Encryption id whose opening sentence is being read back or changed. */
  const [lockPhraseTarget, setLockPhraseTarget] = useState<string | null>(null)

  /** Set after useTagRegistryState so chapter saves can refresh tag registry + usage. */
  const afterChapterSaveRef = useRef<(() => void) | null>(null)
  /** Last-saved `^` heading structure per file — see onAfterSuccessfulSave. */
  const savedHeadingSignatureRef = useRef<Map<string, string>>(new Map())

  // Toolbar container ref for full-width portal rendering
  const toolbarContainerRef = useRef<HTMLDivElement>(null)

  const [expandedChapterFilename, setExpandedChapterFilename] = useState<string | null>(null)

  // Block ID and highlight text to scroll to when the editor mounts after chapter switch
  const pendingScrollRef = useRef<string | null>(null)
  const pendingHighlightRef = useRef<string | null>(null)
  const pendingHighlightOffsetRef = useRef<number | null>(null)
  // Exact scroll offset to restore instead (tab switch back to a cached file)
  const pendingScrollTopRef = useRef<number | null>(null)
  // Caret to restore when the editor mounts, and its signal to take focus —
  // set only when returning to a file the author was already editing.
  const pendingSelectionRef = useRef<AstSelection | null>(null)
  // Last-seen scroll offset per file, captured continuously from the editor's
  // scroll container (ref only — no re-renders on scroll)
  const tabScrollTopsRef = useRef(new Map<string, number>())
  // Where the caret was left in each file, captured on the way out of a tab.
  const tabCaretsRef = useRef(new Map<string, AstSelection>())

  const activeHeadingId = useActiveHeadingTracking(currentContent, currentChapter?.filename, suppressSidebarAutoScroll)

  // Follow passage links and browser history in the loaded file, including a new
  // quote in the same block. The chapter load effect uses the same target on mount.
  useEffect(() => {
    const target = readEditorLinkTarget(location)
    if (!target || !currentChapter || currentChapter.filename !== chapterId) {
      return
    }
    let cancelled = false
    let attempts = 0
    const tryScroll = () => {
      if (cancelled) {
        return
      }
      if (editorRef.current?.scrollToBlock(target.blockId, target.text, target.offset)) {
        return
      }
      if (attempts++ < 20) {
        requestAnimationFrame(tryScroll)
      }
    }
    tryScroll()
    return () => {
      cancelled = true
    }
  }, [location.hash, location.search, location.key, currentChapter?.filename, chapterId])

  // Handle annotation changes (new flexible tagging system)
  const handleAnnotationChange = useCallback(
    async (annotation: Annotation) => {
      if (!bookId || !chapterId) {
        return
      }

      try {
        const response = await apiService.updateAnnotation(bookId, chapterId, annotation)
        // Update current chapter state with new annotations
        if (response.success && response.data) {
          setCurrentChapter((prev) => {
            if (!prev) {
              return null
            }
            return { ...prev, annotations: response.data }
          })
        }
      } catch {
        toast.error('Failed to update tags')
      }
    },
    [bookId, chapterId],
  )

  // Handle annotations repair (when content changes invalidate annotations)
  const handleAnnotationsRepaired = useCallback(
    async (repairedAnnotations: Annotation[], meta?: { expired: Annotation[] }) => {
      if (!bookId || !chapterId) {
        return
      }

      try {
        const response = await apiService.bulkUpdateAnnotations(bookId, chapterId, repairedAnnotations)
        if (response.success && response.data) {
          setCurrentChapter((prev) => {
            if (!prev) {
              return null
            }
            return { ...prev, annotations: response.data }
          })
        }
        if (meta?.expired.length) {
          // The anchored text never came back within the re-attach window — no
          // toast, the author deleted it minutes ago and has moved on.
          logger.warn('Dropped annotations whose re-attach window expired', {
            expiredIds: meta.expired.map((annotation) => annotation.id),
          })
        }
      } catch (error) {
        logger.error('Failed to save repaired annotations', { error })
      }
    },
    [bookId, chapterId],
  )

  /**
   * Called after the OrganizeChapters section-preview editor successfully saves a section.
   * If the updated file is the one currently open in the main editor, refresh its state and
   * remount the LexicalEditor so it picks up the new content.
   */
  const handleSectionContentSaved = useCallback(
    (filename: string, updatedFile: ContentFile) => {
      setChapters((prev) => prev.map((c) => (c.filename === filename ? updatedFile : c)))
      setCurrentChapter((prev) => (prev?.filename === filename ? updatedFile : prev))
      if (currentChapter?.filename === filename && updatedFile.authorContent) {
        setCurrentContent(updatedFile.authorContent)
        setOriginalContent(updatedFile.authorContent)
        // The remount that follows isn't a tab return — don't let it resume a
        // caret captured before the content was rewritten underneath it.
        pendingSelectionRef.current = null
        setExternalContentEpoch((n) => n + 1)
      } else {
        // Saved out-of-band while not open in the main editor — drop the stale cache copy.
        contentCache?.remove(filename)
      }
    },
    [currentChapter?.filename, contentCache],
  )

  const handleAnnotationsBulkUpdate = useCallback(
    async (nextAnnotations: Annotation[]) => {
      if (!bookId || !chapterId) {
        return
      }

      try {
        const response = await apiService.bulkUpdateAnnotations(bookId, chapterId, nextAnnotations)
        if (response.success && response.data) {
          setCurrentChapter((prev) => {
            if (!prev) {
              return null
            }
            return { ...prev, annotations: response.data }
          })
        }
      } catch {
        toast.error('Failed to update tags')
        throw new Error('Failed to update tags')
      }
    },
    [bookId, chapterId],
  )

  // Debug view toggle handler
  const handleToggleDebug = useCallback(() => {
    setDebugMode((prev) => !prev)
  }, [])

  const handleToggleLineNumbers = useCallback(() => {
    setShowLineNumbers((prev) => !prev)
  }, [])

  useEffect(() => {
    if (lineNumbersStorageKey) {
      localStorage.setItem(lineNumbersStorageKey, String(showLineNumbers))
    }
  }, [lineNumbersStorageKey, showLineNumbers])

  // Direction toggle handler
  const handleToggleDirection = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.toggleBlockDirection()
    }
  }, [])

  const handleSetDirection = useCallback((direction: 'ltr' | 'rtl') => {
    if (editorRef.current) {
      editorRef.current.setBlockDirection(direction)
    }
  }, [])

  // Helper function to map book language to AST format
  const getBookMetadata = useCallback(() => {
    if (!book) {
      return { astLang: 'en' as const, bookDir: 'ltr' as const }
    }

    const bookLang = book.metadata?.language || 'en'
    const bookDir = book.metadata?.direction || 'ltr'

    // Map book language to AST language format
    let astLang: 'he' | 'en' | 'mixed'
    if (bookLang === 'he' || bookLang === 'hebrew') {
      astLang = 'he'
    } else if (bookLang === 'mixed') {
      astLang = 'mixed'
    } else {
      astLang = 'en' // Default to English for any other language
    }

    return { astLang, bookDir }
  }, [book])

  // Helper function to create AST with correct book metadata
  const createBookAST = useCallback((): ContentAST => {
    if (!book) {
      return createEmptyAST() // Fallback if book not loaded
    }

    const { astLang, bookDir } = getBookMetadata()
    const ast = createEmptyAST(astLang, bookDir)

    // Note: Font settings are now passed via bookSettings prop to editor
    // and not stored in chapter AST meta

    return ast
  }, [book, getBookMetadata])

  // Load book and chapter list. Wait for auth to settle so the server can
  // return the author (full) view instead of the guest (published-only) view.
  useEffect(() => {
    if (authLoading) {
      return
    }
    let cancelled = false

    const loadBook = async () => {
      if (!bookId) {
        return
      }

      try {
        setLoading(true)
        setError(null)

        const bookResponse = await apiService.getBook(bookId)

        if (cancelled) {
          return
        }
        setBook(bookResponse.data)

        const chaptersData = (await apiService.getChapters(bookId)).data

        if (cancelled) {
          return
        }
        setChapters(chaptersData)

        fetchTagData()

        // Auto-select first chapter if none specified in URL
        if (!chapterId && chaptersData.length > 0) {
          const first = chaptersData[0]
          navigate(`/edit/${bookId}/${first.filename}`, { replace: true })
        }
      } catch (err) {
        if (cancelled) {
          return
        }
        logger.error('Failed to load book data:', err)
        setError('Failed to load book data')
        toast.error('Failed to load book')
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadBook()
    return () => {
      cancelled = true
    }
  }, [bookId, authLoading, user?.id])

  useEffect(() => {
    return () => {
      if (suppressSidebarAutoScrollTimeoutRef.current) {
        window.clearTimeout(suppressSidebarAutoScrollTimeoutRef.current)
      }
    }
  }, [])

  const suppressSidebarAutoScrollTemporarily = useCallback((durationMs = 1200) => {
    setSuppressSidebarAutoScroll(true)
    if (suppressSidebarAutoScrollTimeoutRef.current) {
      window.clearTimeout(suppressSidebarAutoScrollTimeoutRef.current)
    }
    suppressSidebarAutoScrollTimeoutRef.current = window.setTimeout(() => {
      setSuppressSidebarAutoScroll(false)
      suppressSidebarAutoScrollTimeoutRef.current = null
    }, durationMs)
  }, [])

  const prepareContentForAutoSave = useCallback(() => {
    return editorRef.current?.flushPendingSyncToParent() ?? null
  }, [])

  const {
    saving,
    autoSaving,
    hasUnsavedChanges,
    showSavedIndicator,
    setHasUnsavedChanges,
    persistCurrentChapterBeforeNavigation,
    handleSave,
    handleContentChange,
  } = useChapterPersistence({
    bookId,
    currentChapter,
    currentContent,
    readOnly,
    setCurrentChapter,
    setCurrentContent,
    setOriginalContent,
    prepareContentForAutoSave,
    onAfterSuccessfulSave: (filename, saved) => {
      // The post-save refresh rebuilds book-wide views (tag usage, sections TOC,
      // book settings) and each of those endpoints rescans *every* source file
      // in the book — seconds of server time. All of them derive from the file's
      // heading structure, so an edit that leaves the headings alone (which is
      // nearly every autosave, i.e. ordinary prose) cannot have moved any of it.
      //
      // First save of a file this session has no baseline, so it refreshes once.
      const signature = headingSignature(saved)
      const previous = savedHeadingSignatureRef.current.get(filename)
      savedHeadingSignatureRef.current.set(filename, signature)
      if (previous === signature) {
        return
      }
      afterChapterSaveRef.current?.()
    },
  })

  // Live refs so async callbacks (background saves/revalidations resolving after a
  // switch) always see the current file, not the one captured at call time.
  const currentChapterRef = useRef<ContentFile | null>(null)
  currentChapterRef.current = currentChapter
  const currentContentRef = useRef<ContentAST | null>(null)
  currentContentRef.current = currentContent
  const hasUnsavedChangesRef = useRef(false)
  hasUnsavedChangesRef.current = hasUnsavedChanges

  /**
   * Editor onChange → keep the per-file cache in lockstep with every AST sync.
   * This must happen here (not only in the state-sync effect below) because the
   * synchronous flush during a tab switch gets batched away with the incoming
   * file's state updates, so the effect never sees the outgoing file's last AST.
   */
  const handleEditorContentChange = useCallback(
    (ast: ContentAST) => {
      const chapter = currentChapterRef.current
      if (contentCache && chapter && !readOnly) {
        contentCache.set(chapter.filename, chapter, ast, true)
      }
      setCurrentChapter((previous) => {
        if (!previous || previous.filename !== chapter?.filename) {
          return previous
        }
        const annotations = syncWritingReadiness(ast, previous.annotations ?? [])
        return annotations === previous.annotations ? previous : { ...previous, annotations }
      })
      handleContentChange(ast)
    },
    [contentCache, handleContentChange, readOnly],
  )

  // Mirror the live editor state into the per-file cache (covers loads, revision
  // bumps after saves, and external content updates).
  useEffect(() => {
    if (contentCache && currentChapter && currentContent) {
      contentCache.set(currentChapter.filename, currentChapter, currentContent, hasUnsavedChanges)
    }
  }, [contentCache, currentChapter, currentContent, hasUnsavedChanges])

  /**
   * Background revalidation result for a cache-served file. If the server copy
   * diverged (content-session finish, another device) and there are no local
   * edits, reload it; otherwise the local state wins — the same last-write-wins
   * model autosave already implies.
   */
  const handleChapterRevalidated = useCallback((updatedFile: ContentFile) => {
    const chapter = currentChapterRef.current
    if (!chapter || chapter.filename !== updatedFile.filename || hasUnsavedChangesRef.current) {
      return
    }
    const freshAst = updatedFile.authorContent
    if (!freshAst) {
      return
    }
    const localAst = currentContentRef.current
    if (localAst && JSON.stringify(freshAst) === JSON.stringify(localAst)) {
      // Revision moved but the document didn't (a sidecar-only rewrite, say).
      // Adopt the new revision so the next check stops refetching, and leave
      // the editor alone — remounting it here would cost the caret for nothing.
      setCurrentChapter((prev) =>
        prev && prev.filename === updatedFile.filename ? { ...prev, revision: updatedFile.revision } : prev,
      )
      return
    }
    // Adopting the new content remounts the editor, which re-runs its mount-time
    // scroll. Point that at where the author actually is: the pending block is the
    // one this file was *opened* at (a `#blockId` deep link), and reusing it would
    // jump the view back to that heading on every external refresh.
    pendingScrollRef.current = null
    pendingHighlightRef.current = null
    pendingHighlightOffsetRef.current = null
    pendingScrollTopRef.current = tabScrollTopsRef.current.get(updatedFile.filename) ?? null
    // Same reasoning: the content moved under the caret, so don't restore it.
    pendingSelectionRef.current = null

    setChapters((prev) => prev.map((ch) => (ch.filename === updatedFile.filename ? updatedFile : ch)))
    setCurrentChapter((prev) => (prev && prev.filename === updatedFile.filename ? updatedFile : prev))
    setCurrentContent(freshAst)
    setOriginalContent(freshAst)
    setExternalContentEpoch((n) => n + 1)
  }, [])

  const applyLoadedChapterToState = useCallback(
    (
      loadedChapter: ContentFile,
      options: {
        chapterAST?: ContentAST
        scrollBlockId?: string | null
        scrollHighlightText?: string | null
        scrollHighlightOffset?: number | null
        scrollTop?: number | null
        closeSidebar?: boolean
        restoreDirty?: boolean
      } = {},
    ) => {
      const chapterAST = options.chapterAST || loadedChapter.authorContent || createBookAST()

      setCurrentChapter(loadedChapter)
      setExpandedChapterFilename(loadedChapter.filename)

      setChapters((prev) => {
        const hasExistingChapter = prev.some((ch) => ch.filename === loadedChapter.filename)
        if (!hasExistingChapter) {
          return [...prev, loadedChapter]
        }

        return prev.map((ch) => {
          if (ch.filename !== loadedChapter.filename) {
            return ch
          }
          return {
            ...loadedChapter,
            tokenCount: loadedChapter.tokenCount ?? ch.tokenCount,
          }
        })
      })

      setOriginalContent(chapterAST)
      setCurrentContent(chapterAST)
      // restoreDirty: a cache-restored file whose save never landed — hand the
      // retry back to the autosave loop.
      setHasUnsavedChanges(options.restoreDirty ?? false)
      pendingScrollRef.current = options.scrollBlockId ?? null
      pendingHighlightRef.current = options.scrollHighlightText ?? null
      pendingHighlightOffsetRef.current = options.scrollHighlightOffset ?? null
      pendingScrollTopRef.current = options.scrollTop ?? null
      // A remembered caret resumes the tab where the author left it. Skipped when
      // the navigation names a target block (search hit, heading link, deep link):
      // there the author asked to be somewhere specific, not where they last were.
      pendingSelectionRef.current = options.scrollBlockId
        ? null
        : (tabCaretsRef.current.get(loadedChapter.filename) ?? null)
      // Seed the scroll memory so a file the user never scrolls still restores to
      // its true position (any scroll — user or programmatic — overwrites this).
      tabScrollTopsRef.current.set(loadedChapter.filename, options.scrollTop ?? 0)

      if (options.closeSidebar) {
        setSidebarOpen(false)
      }

      return chapterAST
    },
    [],
  )

  // Bridge from useChapterNavigation back to the tabs hook (defined below) without
  // creating a render-order cycle. The ref is set right after the tabs hook is constructed.
  const getTabScrollAnchorRef = useRef<((filename: string) => string | null) | null>(null)
  const getTabScrollAnchorForNavigation = useCallback((filename: string): string | null => {
    return getTabScrollAnchorRef.current?.(filename) ?? null
  }, [])

  const getTabScrollTop = useCallback((filename: string): number | null => {
    return tabScrollTopsRef.current.get(filename) ?? null
  }, [])

  const {
    chapterTransitioning,
    searchChapterTransitioning,
    tocChapterLoadingFilename,
    revalidateCachedChapter,
    handleChapterSelect,
    handleHeadingNavigation: handleHeadingNavigationInternal,
    handleSearchNavigate: handleSearchNavigateInternal,
  } = useChapterNavigation({
    bookId,
    chapterId,
    chapters,
    currentChapter,
    currentContent,
    isMobile,
    navigate,
    editorRef,
    createBookAST,
    setMobileSidePaneOpen,
    suppressSidebarAutoScrollTemporarily,
    persistCurrentChapterBeforeNavigation,
    applyLoadedChapterToState,
    getTabScrollAnchor: getTabScrollAnchorForNavigation,
    getTabScrollTop,
    contentCache,
    onChapterRevalidated: handleChapterRevalidated,
  })

  /**
   * Re-check the open file whenever the window comes back. The cache check on
   * a tab switch only covers files you navigate *back* to; a file left open
   * while something else rewrites it — a content session editing the `.md` on
   * disk — would stay stale until the next switch, and the next autosave would
   * write that stale copy back over it.
   *
   * Skipped while there are local edits: those win, exactly as on the tab-switch
   * path, and revalidating would only race the autosave that is about to fire.
   */
  useEffect(() => {
    if (readOnly) {
      return
    }
    const revalidateOpenFile = () => {
      const chapter = currentChapterRef.current
      if (!chapter || hasUnsavedChangesRef.current || document.hidden) {
        return
      }
      revalidateCachedChapter(chapter.filename, chapter.revision)
    }
    const handleVisible = () => {
      if (!document.hidden) {
        revalidateOpenFile()
      }
    }
    window.addEventListener('focus', revalidateOpenFile)
    document.addEventListener('visibilitychange', handleVisible)
    return () => {
      window.removeEventListener('focus', revalidateOpenFile)
      document.removeEventListener('visibilitychange', handleVisible)
    }
  }, [readOnly, revalidateCachedChapter])

  const isAuthor = Boolean(book && user && !authLoading && book.author._id === user.id)
  const { proposals, proposalError } = useContentProposals(bookId, isAuthor)
  const fileProposals = useMemo(
    () =>
      proposals
        .filter(
          (item) =>
            item.filename === currentChapter?.filename && (item.status === 'pending' || item.status === 'applying'),
        )
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [proposals, currentChapter?.filename],
  )

  // The assistant pane also hosts external proposals, available without a model connection.
  const [assistantAvailable, setAssistantAvailable] = useState(false)

  useEffect(() => {
    if (!bookId || !isAuthor) {
      return
    }
    let cancelled = false
    void fetchAssistantAvailability(bookId).then((available) => {
      if (!cancelled) {
        setAssistantAvailable(available)
      }
    })
    return () => {
      cancelled = true
    }
  }, [bookId, isAuthor])

  // ── Content history ──
  // Only available where the sibling content repo is checked out (the author's
  // machine); everywhere else the entry points stay hidden rather than erroring.
  const historyAvailable = useHistoryAvailability(bookId, isAuthor)

  // ── Editor tabs (IDE-style multi-file editing) ──
  const tabsEnabled = isAuthor && !isMobile
  const knownFilenames = useMemo(() => chapters.map((c) => c.filename), [chapters])
  const {
    tabs,
    openOrActivate: openOrActivateTab,
    closeTab,
    setScrollAnchor: setTabScrollAnchor,
    getScrollAnchor: getTabScrollAnchor,
    renameTabFilename,
    reorderTabs,
    getMruFilenames,
  } = useEditorTabs({
    bookId,
    activeFilename: chapterId ?? null,
    knownFilenames,
    enabled: tabsEnabled,
  })

  // Wire the tabs-hook scroll lookup back through the ref so useChapterNavigation can read it.
  getTabScrollAnchorRef.current = getTabScrollAnchor

  // An undo stack is only worth its memory while its tab is open — drop the rest
  // (closed, evicted at the tab cap, or deleted on another device). Where tabs
  // are off (mobile) that leaves just the file on screen, so browsing a book
  // doesn't accumulate a history stack per file visited.
  useEffect(() => {
    if (!undoHistoryCache) {
      return
    }
    const open = tabsEnabled ? tabs.map((t) => t.filename) : []
    if (currentChapter?.filename) {
      open.push(currentChapter.filename)
    }
    if (open.length === 0) {
      return
    }
    undoHistoryCache.retainOnly(open)
  }, [tabs, tabsEnabled, currentChapter?.filename, undoHistoryCache])

  /**
   * Capture where the author was in the outgoing chapter: the visible heading as
   * its scroll memory, and the caret so the tab can be resumed rather than just
   * re-displayed. Must run synchronously in the click handler — by the time the
   * new chapter's editor has remounted, activeHeadingId reflects the new
   * chapter's DOM and the old editor is gone with its selection.
   */
  const snapshotOutgoingTabPosition = useCallback(() => {
    if (!tabsEnabled || !currentChapter) {
      return
    }
    if (activeHeadingId) {
      setTabScrollAnchor(currentChapter.filename, activeHeadingId)
    }
    const caret = editorRef.current?.getUndoRedoManager().getCurrentSelection()
    if (caret) {
      tabCaretsRef.current.set(currentChapter.filename, caret)
    }
  }, [tabsEnabled, currentChapter, activeHeadingId, setTabScrollAnchor])

  // Heading nav doesn't go through applyLoadedChapter's closeSidebar path
  // (especially the same-chapter scroll branch), so close the drawer here.
  // Clicking a heading in a *different* chapter opens that chapter in its own tab
  // (an existing tab is reused). Same-chapter clicks just scroll.
  const handleHeadingNavigation = useCallback(
    (chapter: ContentFile, headingBlockId: string) => {
      if (isMobile) {
        setSidebarOpen(false)
      }
      if (tabsEnabled && chapter.filename !== currentChapter?.filename) {
        snapshotOutgoingTabPosition()
        openOrActivateTab(chapter.filename)
      }
      return handleHeadingNavigationInternal(chapter, headingBlockId)
    },
    [
      handleHeadingNavigationInternal,
      isMobile,
      tabsEnabled,
      openOrActivateTab,
      currentChapter?.filename,
      snapshotOutgoingTabPosition,
    ],
  )

  // Every sidebar click opens the file in its own tab (an existing tab is reused;
  // the LRU tab is evicted beyond the cap).
  const handleSidebarChapterSelect = useCallback(
    (chapter: ContentFile) => {
      if (chapter.filename !== currentChapter?.filename) {
        snapshotOutgoingTabPosition()
      }
      if (tabsEnabled) {
        openOrActivateTab(chapter.filename)
      }
      void handleChapterSelect(chapter)
    },
    [tabsEnabled, openOrActivateTab, handleChapterSelect, currentChapter?.filename, snapshotOutgoingTabPosition],
  )

  // Expose the file switch for the editor probe (development only). Driving it
  // through the sidebar DOM would couple the probe to the sidebar's markup, and
  // a page.goto() would drop the in-memory cache the probe needs to exercise.
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return
    }
    ;(window as any).__probeSelectChapter = (filename: string) => {
      const target = chapters.find((ch) => ch.filename === filename)
      if (!target) {
        throw new Error(`__probeSelectChapter: no chapter named ${filename}`)
      }
      handleSidebarChapterSelect(target)
    }
    return () => {
      delete (window as any).__probeSelectChapter
    }
  }, [chapters, handleSidebarChapterSelect])

  const handleTabBarSelect = useCallback(
    (filename: string) => {
      if (filename === currentChapter?.filename) {
        return
      }
      const chapter = chapters.find((c) => c.filename === filename)
      if (!chapter) {
        return
      }
      snapshotOutgoingTabPosition()
      void handleChapterSelect(chapter)
    },
    [currentChapter?.filename, chapters, handleChapterSelect, snapshotOutgoingTabPosition],
  )

  const handleTabBarClose = useCallback(
    (filename: string) => {
      const wasActive = filename === currentChapter?.filename
      const { nextActiveFilename, rejected } = closeTab(filename)

      // `closeTab` refuses to close the last tab — keep the editor as-is.
      if (rejected || !wasActive || !nextActiveFilename) {
        return
      }

      const next = chapters.find((c) => c.filename === nextActiveFilename)
      if (next) {
        void handleChapterSelect(next)
      }
    },
    [chapters, closeTab, currentChapter?.filename, handleChapterSelect],
  )

  // ⌥W closes the current tab (⌘W belongs to the browser). Complements the ⌥Tab switcher.
  useEffect(() => {
    if (!tabsEnabled) {
      return
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && !e.metaKey && !e.ctrlKey && e.code === 'KeyW') {
        if (document.querySelector('.MuiModal-root')) {
          return
        }
        e.preventDefault()
        e.stopPropagation()
        const filename = currentChapterRef.current?.filename
        if (filename) {
          handleTabBarClose(filename)
        }
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [tabsEnabled, handleTabBarClose])

  const {
    chapterSectionsFromServer,
    chapterSectionEntries,
    chapterGroups,
    entryByKey,
    handleSectionDragEnd,
    persistFlatSectionsOrder,
    persistElementSectionOrder,
    runChapterSectionsRefreshAfterSave,
    refetchChapterSectionsFromServer,
    addChapterBefore: addChapterGroupBefore,
    renameChapter: renameChapterGroup,
    setChapterNote: setChapterGroupNote,
    deleteChapter: deleteChapterGroup,
    upsertSectionDependency,
    removeSectionDependency,
  } = useChapterSectionsBook({
    bookId,
    isAuthor,
    chapters,
    currentChapter,
    currentContent,
    book,
    setBook,
    bookRef,
    chaptersRef,
  })

  // --- Continuous all-placed-sections editing view (flat structure) ---

  const [continuousView, setContinuousView] = useState(false)

  const placedSectionEntries = useMemo(() => {
    const order = book?.settings.flatSectionsOrder ?? []
    return order.map((k) => entryByKey.get(k)).filter((e): e is ChapterSectionEntry => e != null)
  }, [book?.settings.flatSectionsOrder, entryByKey])

  // Leaving flat structure (or losing all placed sections) closes the continuous view.
  useEffect(() => {
    if (continuousView && book?.settings.structure !== 'flat') {
      setContinuousView(false)
    }
  }, [continuousView, book?.settings.structure])

  // --- Dependency editing from the editor surface ---

  const depSectionOptions = useMemo(() => {
    const out: { key: string; label: string; groupTitle?: string }[] = []
    for (const group of chapterGroups) {
      const isUnattached = group.chapter.id === '__unattached__'
      const groupTitle = isUnattached ? 'לא משויך' : group.chapter.title || 'ללא כותרת'
      for (const entry of group.entries) {
        const key = chapterSectionKey(entry.chapter.filename, entry.headingBlockId)
        out.push({ key, label: entry.displayText, groupTitle })
      }
    }
    return out
  }, [chapterGroups])

  /**
   * Dependencies are stored as section-key pairs and are the same set in either
   * structure; only the sequence a violation is judged against differs, and that is
   * resolved inside the hook. Everything downstream is mode-agnostic.
   */
  const sectionDependencies = useSectionDependencies(book, entryByKey)

  /**
   * The ChapterSection key for the section the cursor is currently in (if any).
   * Used to pre-select the section when opening the Organize Chapters dialog.
   */
  const currentSectionKey = useMemo<string | null>(() => {
    if (!currentChapter || !currentContent || !activeHeadingId) {
      return null
    }
    const headingBlockId = findEnclosingChapterSectionHeadingBlockId(currentContent, activeHeadingId)
    if (!headingBlockId) {
      return null
    }
    return chapterSectionKey(currentChapter.filename, headingBlockId)
  }, [currentChapter, currentContent, activeHeadingId])

  const handleAskAssistant = useCallback(
    (selection: { markedText?: string; startBlockId?: string }) => {
      setAssistant({ open: true, markedText: selection.markedText, startBlockId: selection.startBlockId })
      // Desktop keeps the conversation in the side pane; only mobile opens a drawer.
      if (!isMobile) {
        sidePaneRef.current?.showAssistant()
      }
    },
    [isMobile],
  )

  const handleAddDependencyFromEditor = useCallback(
    (args: { direction: 'before' | 'after'; currentSectionHeadingBlockId: string | null; selectedText: string }) => {
      const currentKey =
        currentChapter && args.currentSectionHeadingBlockId
          ? chapterSectionKey(currentChapter.filename, args.currentSectionHeadingBlockId)
          : ''
      const trimmed = args.selectedText.trim().slice(0, 500)
      setAddDepDialog({
        initialFromKey: args.direction === 'before' ? currentKey : '',
        initialToKey: args.direction === 'after' ? currentKey : '',
        initialReason: trimmed,
      })
    },
    [currentChapter],
  )

  const {
    searchQuery,
    selectedTagIds,
    searchResults,
    searchLoading,
    searchSearched,
    keyboardPreviewOpen,
    mobilePreviewOpen,
    mobilePreviewHeightRatio,
    mobilePreviewDragging,
    selectedPreviewResult,
    previewPopupOffset,
    previewPopupDragging,
    resolvedPreviewAst,
    resolvedPreviewError,
    resolvedPreviewLoading,
    previewDirty,
    invalidatePreviewFile,
    previewSaving,
    previewRecentlySaved,
    setSearchQuery,
    setMobilePreviewOpen,
    handleSearchClear,
    handleCloseDesktopPreview,
    handlePreviewEditChange,
    handlePreviewEditSave,
    handleTagsChange,
    handleSearchNavigate,
    handleMobilePreviewSelection,
    handleDesktopPreviewSelection,
    handleEnterSearchSelection,
    handleStepSidePaneSearchSelection,
    handlePreviewPopupPointerDown,
    handleMobilePreviewPointerDown,
    getSearchResultKey,
  } = useBookSearchController({
    bookId,
    isMobile,
    currentChapter,
    currentContent,
    onNavigateSearchResult: handleSearchNavigateInternal,
    onPreviewContentSaved: handleSectionContentSaved,
  })

  // Close the search preview popup, offering to save unsaved edits (getAST flushes the
  // popup editor's pending debounced changes first).
  const handleSearchPreviewClose = useCallback(() => {
    void handleCloseDesktopPreview(() => searchPreviewEditorRef.current?.getAST() ?? null)
  }, [handleCloseDesktopPreview])

  // Clicking a search result row opens its source file in a tab and jumps to the match.
  // Peeking without leaving the current chapter is the row's preview icon instead.
  const handleSearchResultOpen = useCallback(
    (filename: string, blockId: string, matchText: string, matchStart: number) => {
      if (filename !== currentChapter?.filename) {
        snapshotOutgoingTabPosition()
      }
      if (tabsEnabled) {
        openOrActivateTab(filename)
      }
      void handleSearchNavigate(filename, blockId, matchText, matchStart)
    },
    [tabsEnabled, openOrActivateTab, handleSearchNavigate, currentChapter?.filename, snapshotOutgoingTabPosition],
  )

  /** Take the author to a block the assistant named, highlighted, exactly like a search hit. */
  const handleGoTo = useCallback(
    (filename: string, blockId: string, text: string) => {
      handleSearchResultOpen(filename, blockId, text, 0)
    },
    [handleSearchResultOpen],
  )

  const handleOpenEmbedSource = useCallback(
    (filename: string, blockId: string) => handleSearchResultOpen(filename, blockId, '', 0),
    [handleSearchResultOpen],
  )

  const [applyingProposal, setApplyingProposal] = useState(false)
  const handleApplyProposal = useCallback(
    async (apply: () => Promise<void>, filename: string) => {
      const pending = editorRef.current?.flushPendingSyncToParent()
      const previewPending = searchPreviewEditorRef.current?.flushPendingSyncToParent()
      if (
        pending ||
        previewPending ||
        hasUnsavedChangesRef.current ||
        previewDirty ||
        previewSaving ||
        saving ||
        autoSaving ||
        contentCache?.get(filename)?.dirty
      ) {
        throw new Error('יש לשמור את העריכות הפתוחות לפני אישור ההצעה')
      }
      setApplyingProposal(true)
      try {
        await apply()
        contentCache?.remove(filename)
        invalidatePreviewFile(filename)
        const chapter = currentChapterRef.current
        if (chapter && bookId) {
          const fresh = await apiService.getChapter(bookId, chapter.filename)
          handleChapterRevalidated(fresh.data)
        }
        await refetchChapterSectionsFromServer()
      } finally {
        setApplyingProposal(false)
      }
    },
    [
      bookId,
      contentCache,
      invalidatePreviewFile,
      previewDirty,
      previewSaving,
      saving,
      autoSaving,
      handleChapterRevalidated,
      refetchChapterSectionsFromServer,
    ],
  )

  const handleInlineProposalAction = useCallback<InlineProposalReview['onAction']>(
    async (item, action, markdown, changeId) => {
      if (!bookId) {
        return
      }
      const execute = async () => {
        await updateContentProposal(bookId, item, action, markdown, changeId)
      }
      if (action === 'accept') {
        await handleApplyProposal(execute, item.filename)
      } else {
        await execute()
      }
    },
    [bookId, handleApplyProposal],
  )

  const handleOpenPreviewEmbedSource = useCallback(
    async (filename: string, blockId: string) => {
      const editor = searchPreviewEditorRef.current
      const pending = editor?.flushPendingSyncToParent()
      const changed = pending ?? (previewDirty ? editor?.getAST() : null)
      if (changed && !(await handlePreviewEditSave(changed))) {
        return
      }
      handleOpenEmbedSource(filename, blockId)
    },
    [previewDirty, handlePreviewEditSave, handleOpenEmbedSource],
  )

  const {
    tagColorMap,
    tagRegistry,
    tagRegistryLoaded,
    tagUsageFlatCounts,
    tagMatchedChapters,
    applyTagRegistry,
    fetchTagData,
  } = useTagRegistryState(bookId, selectedTagIds)

  // The writer-launch reminder is for the live site only — skip it on localhost.
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')

  const flaggedSpots = useFlaggedSpotsPrompt({
    enabled: !isLocalhost,
    bookId,
    chapterId,
  })

  useEffect(() => {
    afterChapterSaveRef.current = () => {
      void fetchTagData()
      runChapterSectionsRefreshAfterSave()
    }
    return () => {
      afterChapterSaveRef.current = null
    }
  }, [bookId, fetchTagData, runChapterSectionsRefreshAfterSave])

  useEffect(() => {
    if (!mobileSidePaneOpen) {
      setMobilePreviewOpen(false)
    }
  }, [mobileSidePaneOpen, setMobilePreviewOpen])

  // Handle chapter creation
  const handleCreateChapter = async () => {
    if (!bookId || !book) {
      return
    }

    const title = prompt('Enter chapter title:')
    if (!title?.trim()) {
      return
    }

    try {
      const newChapter = await apiService.createChapter(bookId, {
        title: title.trim(),
        authorContent: createBookAST(),
        order: chapters.length + 1,
      })

      const emptyAST = createBookAST()
      applyLoadedChapterToState(newChapter.data, { chapterAST: emptyAST })
      navigate(`/edit/${bookId}/${newChapter.data.filename}`)
    } catch (err) {
      logger.error('Failed to create chapter:', err)
      toast.error('Failed to create chapter')
    }
  }

  const handleOpenTagChapter = useCallback((chapter: ContentFile) => {
    setTagTargetChapter(chapter)
  }, [])

  const handleSaveChapterFileTags = useCallback(
    async (fileTags: string[]) => {
      if (!bookId || !tagTargetChapter) {
        return
      }
      try {
        await apiService.updateChapter(bookId, tagTargetChapter.filename, { fileTags })

        setChapters((prev) => prev.map((ch) => (ch.filename === tagTargetChapter.filename ? { ...ch, fileTags } : ch)))
        setCurrentChapter((prev) =>
          prev && prev.filename === tagTargetChapter.filename ? { ...prev, fileTags } : prev,
        )

        fetchTagData()
        setTagTargetChapter(null)
      } catch (err) {
        logger.error('Failed to tag chapter:', err)
        toast.error('Failed to save chapter tags')
      }
    },
    [bookId, tagTargetChapter, fetchTagData],
  )

  // Handle chapter deletion
  const handleDeleteChapter = async (chapter: ContentFile) => {
    if (!window.confirm(`Are you sure you want to delete "${chapter.title}"?`)) {
      return
    }
    if (!bookId) {
      return
    }

    try {
      await apiService.deleteChapter(bookId, chapter.filename)
      contentCache?.remove(chapter.filename)
      tabScrollTopsRef.current.delete(chapter.filename)
      tabCaretsRef.current.delete(chapter.filename)
      setChapters((prev) => prev.filter((ch) => ch.filename !== chapter.filename))

      // Remove from tab bar; this also returns which tab should activate next.
      const closeResult = tabsEnabled ? closeTab(chapter.filename) : { nextActiveFilename: null }

      if (currentChapter?.filename !== chapter.filename) {
        return
      }

      if (tabsEnabled) {
        if (closeResult.nextActiveFilename) {
          navigate(`/edit/${bookId}/${closeResult.nextActiveFilename}`)
        } else {
          setCurrentChapter(null)
          setCurrentContent(null)
          setOriginalContent(null)
          navigate(`/edit/${bookId}`)
        }
      } else {
        const remainingChapters = chapters.filter((ch) => ch.filename !== chapter.filename)
        if (remainingChapters.length > 0) {
          setCurrentChapter(remainingChapters[0])
          navigate(`/edit/${bookId}/${remainingChapters[0].filename}`)
        } else {
          setCurrentChapter(null)
          navigate(`/edit/${bookId}`)
        }
      }
    } catch (err) {
      logger.error('Failed to delete chapter:', err)
      toast.error('Failed to delete chapter')
    }
  }

  // Handle source file rename (title + filename on disk)
  const handleRenameChapter = async (chapter: ContentFile) => {
    if (!bookId) {
      return
    }

    const newTitle = prompt('Enter new source file title:', chapter.title)
    if (!newTitle?.trim()) {
      return
    }

    if (newTitle.trim() === chapter.title) {
      return
    }

    try {
      const response = await apiService.renameSourceFile(bookId, chapter.filename, newTitle.trim())

      const updated = response.data
      const filenameChanged = updated.filename !== chapter.filename

      setChapters((prev) => prev.map((ch) => (ch.filename === chapter.filename ? updated : ch)))

      if (currentChapter?.filename === chapter.filename) {
        setCurrentChapter(updated)
      }

      if (filenameChanged) {
        // The cache is filename-keyed — drop the old entry so a future file with
        // the same name can't resolve to this content.
        contentCache?.remove(chapter.filename)
        // The undo stack follows the file rather than being dropped — a rename
        // isn't an edit, and the author's ⌘Z should still reach past it.
        undoHistoryCache?.rename(chapter.filename, updated.filename)
        tabScrollTopsRef.current.delete(chapter.filename)
        tabCaretsRef.current.delete(chapter.filename)
        // Carry any open tab over to the new filename so it doesn't get pruned as "deleted".
        if (tabsEnabled) {
          renameTabFilename(chapter.filename, updated.filename)
        }
        // If the renamed file is currently open, update the URL to its new filename.
        if (currentChapter?.filename === chapter.filename) {
          navigate(`/edit/${bookId}/${updated.filename}`, { replace: true })
        }
        await refetchChapterSectionsFromServer()
        toast.success(`Renamed to "${updated.title}"`)
      }
    } catch (err) {
      logger.error('Failed to rename source file:', err)
      toast.error('Failed to rename source file')
    }
  }

  // Handle chapter reordering via drag and drop
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event

    if (!over || active.id === over.id || !bookId) {
      return
    }

    const oldIndex = chapters.findIndex((ch) => ch.filename === active.id)
    const newIndex = chapters.findIndex((ch) => ch.filename === over.id)

    if (oldIndex === -1 || newIndex === -1) {
      return
    }

    // Optimistically update UI
    const reorderedItems = [...chapters]
    const [movedItem] = reorderedItems.splice(oldIndex, 1)
    reorderedItems.splice(newIndex, 0, movedItem)

    // Update order field within the full chapter list
    const updatedItems = reorderedItems.map((ch, index) => ({
      ...ch,
      order: index + 1,
    }))

    const previousChapters = chapters
    const previousCurrentChapter = currentChapter
    setChapters(updatedItems)
    setCurrentChapter((prev) => {
      if (!prev) {
        return prev
      }
      const updatedCurrent = updatedItems.find((ch) => ch.filename === prev.filename)
      if (!updatedCurrent) {
        return prev
      }
      // Reorder updates chapter frontmatter, so cached revision becomes stale.
      // Clearing it avoids false conflict errors on the next save.
      return {
        ...prev,
        order: updatedCurrent.order,
        revision: undefined,
      }
    })

    try {
      const chapterOrders = updatedItems.map((ch) => ({
        filename: ch.filename,
        order: ch.order,
      }))

      await apiService.reorderChapters(bookId, chapterOrders)
      await refetchChapterSectionsFromServer()
    } catch (err) {
      logger.error('Failed to reorder:', err)
      toast.error('Failed to reorder')
      // Revert on error
      setChapters(previousChapters)
      setCurrentChapter(previousCurrentChapter)
    }
  }

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px movement before drag starts
      },
    }),
  )

  // HTML view mode toggle removed - AST editor handles this internally

  // Extract headings from chapter AST content (only from clean section)
  // For the currently active chapter, use live currentContent so edits reflect immediately
  const getChapterHeadings = (chapter: ContentFile) => {
    const ast = chapter.filename === currentChapter?.filename && currentContent ? currentContent : chapter.authorContent
    if (!ast) {
      return []
    }

    // Only get headings from clean section (before section divider)
    const cleanBlocks = ast.blocks

    return cleanBlocks
      .filter((block) => block.type === 'heading')
      .map((block) => {
        const heading = block as HeadingBlock
        return {
          text: getBlockText(heading),
          level: heading.level,
          id: heading.id,
        }
      })
  }

  const mobileTitleHierarchy = useMemo(() => {
    const hierarchy: string[] = []

    const pushUnique = (value?: string) => {
      const normalized = (value || '').trim()
      if (!normalized || hierarchy[hierarchy.length - 1] === normalized) {
        return
      }
      hierarchy.push(normalized)
    }

    pushUnique(book?.title || 'Untitled Book')
    pushUnique(currentChapter?.title)

    if (!currentChapter || !activeHeadingId) {
      return hierarchy
    }

    const chapterHeadings = getChapterHeadings(currentChapter)
    const activeHeadingIndex = chapterHeadings.findIndex((heading) => heading.id === activeHeadingId)
    if (activeHeadingIndex < 0) {
      return hierarchy
    }

    const activeHeadingStack: Array<{ level: number; text: string }> = []
    for (let i = 0; i <= activeHeadingIndex; i++) {
      const heading = chapterHeadings[i]
      while (
        activeHeadingStack.length > 0 &&
        activeHeadingStack[activeHeadingStack.length - 1].level >= heading.level
      ) {
        activeHeadingStack.pop()
      }
      activeHeadingStack.push({ level: heading.level, text: heading.text })
    }

    activeHeadingStack.forEach((heading) => pushUnique(stripChapterSectionHeadingPrefix(heading.text)))
    return hierarchy
  }, [book?.title, currentChapter, activeHeadingId, currentContent])

  // Set read-only mode for public reading or non-authors
  // Wait for auth + book to load before deciding — otherwise the UI dims during loading
  useEffect(() => {
    if (authLoading || !book) {
      return
    }
    setReadOnly(!isAuthor)
  }, [isAuthor, authLoading, book])

  useEffect(() => {
    if (readOnly) {
      editorRef.current?.blur()
    }
  }, [readOnly])

  useEffect(() => {
    if (!isMobile || !mobileSidePaneOpen) {
      return
    }

    const timer = window.setTimeout(() => {
      mobileSearchRef.current?.focusInput()
    }, 50)

    return () => window.clearTimeout(timer)
  }, [isMobile, mobileSidePaneOpen])

  const { handleChapterPublish, handleChapterSectionPublish, chapterSectionPublishBusyKey, refreshChapterState } =
    usePublishingActions({
      bookId,
      currentChapter,
      currentContent,
      hasUnsavedChanges,
      setHasUnsavedChanges,
      setChapters,
      setCurrentChapter,
      setPublishOpen,
      setPublishTarget,
      handleChapterSelect,
    })

  // Handle book settings update
  const handleBookUpdated = useCallback((updatedBook: Book) => {
    setBook(updatedBook)
    // Note: Font settings are now passed via bookSettings prop to editor
    // No need to update AST content
  }, [])

  // Generate AST content for the editor
  const getEditorContent = (): ContentAST | null => currentContent

  /**
   * The tab's undo manager. The editor builds one at mount, which is exactly why
   * history used to die on every file switch — hand it the tab's existing one
   * instead.
   *
   * Keyed on the same tuple as the editor's own `key`, so the manager's identity
   * changes only when the editor remounts. Re-acquiring on every render would be
   * worse than useless: between a keystroke and the 150ms sync debounce the
   * manager's AST is ahead of `currentContent`, and the freshness check would
   * read that as staleness and throw the history away mid-edit.
   */
  const undoRedoManager = useMemo(() => {
    const ast = currentContentRef.current
    if (!undoHistoryCache || !currentChapter || !ast) {
      return undefined
    }
    return undoHistoryCache.acquire(currentChapter.filename, ast)
  }, [undoHistoryCache, currentChapter?.filename, externalContentEpoch])

  // Handle publish action
  // const handlePublish = () => {
  //   setPublishingModal({ open: true })
  // }

  // Cmd+/ shortcut to focus search
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Physical Slash key (layout-independent — Hebrew remaps the '/' character).
      if (e.code === 'Slash' && e.metaKey && !e.shiftKey && !e.altKey) {
        e.preventDefault()
        e.stopPropagation()
        if (isMobile) {
          setMobilePreviewOpen(false)
          setMobileSidePaneOpen(true)
        } else {
          sidePaneRef.current?.focusSearch()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [isMobile, setMobilePreviewOpen])

  bookRef.current = book
  chaptersRef.current = chapters

  if (loading || authLoading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading...
        </Typography>
      </Container>
    )
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate('/dashboard')}>Back to Dashboard</Button>
      </Container>
    )
  }

  // Header actions for author mode (styled as menu buttons like Google Docs)
  const menuButtonSx = {
    textTransform: 'none',
    color: 'var(--text-primary)',
    fontWeight: 400,
    fontSize: '14px',
    px: 1.5,
    minWidth: 'auto',
    '&:hover': { bgcolor: 'var(--bg-hover)' },
  }

  const handleOpenSearch = () => {
    if (isMobile) {
      setMobilePreviewOpen(false)
      setMobileSidePaneOpen(true)
      return
    }
    sidePaneRef.current?.focusSearch()
  }

  const headerActionProps = {
    isAuthor,
    isMobile,
    saving,
    autoSaving,
    hasUnsavedChanges,
    menuButtonSx,
    contentSource: book?.contentSource,
    onSave: handleSave,
    onOpenChapterList: () => setSidebarOpen(true),
    onOpenSearch: handleOpenSearch,
    onOpenBookSettings: () => setBookSettingsOpen(true),
    onOpenHistory: historyAvailable ? () => setHistoryOpen(true) : undefined,
    onOpenAssistant: isAuthor && assistantAvailable ? () => handleAskAssistant({}) : undefined,
  }

  const renderHeaderActions = (section: 'full' | 'primary' | 'menu' = 'full') => (
    <BookEditorHeaderActions {...headerActionProps} section={section} />
  )

  const headerActions = renderHeaderActions()

  const headerActionsBeforeTitle =
    isMobile && book?.metadata?.direction === 'rtl' ? renderHeaderActions('primary') : undefined

  const headerActionsAfterTitle =
    isMobile && book?.metadata?.direction === 'rtl' ? renderHeaderActions('menu') : undefined

  return (
    <Box
      sx={{
        // Use dynamic viewport height so the container tracks the visible area as the mobile
        // URL bar shows/hides. With static 100vh the box is taller than the visual viewport
        // while the URL bar is shown, producing a body-level scroll that drags the pinned
        // header/toolbar off-screen.
        height: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'var(--bg-app)',
      }}
      dir={book?.metadata?.direction || 'ltr'}
    >
      <Modal open={applyingProposal} aria-label="מחיל הצעה">
        <Box
          tabIndex={-1}
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            p: 3,
            borderRadius: 2,
          }}
        >
          <CircularProgress size={24} />
          <Typography>מחיל הצעה ומרענן את התוכן…</Typography>
        </Box>
      </Modal>
      {/* Minimal Header (Google Docs style) */}
      <Header
        title={book?.title || 'Untitled Book'}
        mobileTitleHierarchy={mobileTitleHierarchy}
        titleFontFamily={book?.settings.titleFontFamily || 'SBL Hebrew'}
        titleDirection={book?.metadata?.direction || 'ltr'}
        actionsBeforeTitle={headerActionsBeforeTitle}
        actionsAfterTitle={headerActionsAfterTitle}
        actions={headerActions}
        hideNavigation={true}
        hideBrandOnMobile={true}
        hideHiddenNavigationMenuOnMobile={true}
        toolbarSlot={
          // Full-width toolbar container (portal renders toolbar here)
          isAuthor && currentChapter ? (
            <Box
              ref={toolbarContainerRef}
              sx={{
                borderTop: '1px solid var(--toolbar-border)',
                minHeight: { xs: '40px', md: '49px' },
                opacity: readOnly ? 0.35 : 1,
                pointerEvents: readOnly ? 'none' : 'auto',
                transition: 'opacity 0.2s ease',
              }}
            />
          ) : null
        }
      />

      {/* 3-column content area */}
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          overflow: 'hidden',
        }}
      >
        {/* Sidebar content */}
        <EditorSidebar
          bookDir={book?.metadata?.direction || 'ltr'}
          isAuthor={isAuthor}
          isMobile={isMobile}
          sidebarOpen={sidebarOpen}
          menuButtonSx={menuButtonSx}
          onSidebarClose={() => setSidebarOpen(false)}
          onCreateChapter={handleCreateChapter}
          onOpenBookSettings={() => {
            setBookSettingsOpen(true)
            setSidebarOpen(false)
          }}
          renderChapterSectionsToc={
            chapterSectionsFromServer === null || chapterSectionEntries.length === 0
              ? undefined
              : () => (
                  <ChapterSectionsToc
                    chapterGroups={chapterGroups}
                    chapterSectionEntries={chapterSectionEntries}
                    book={book}
                    bookId={bookId}
                    currentChapter={currentChapter}
                    activeHeadingId={activeHeadingId}
                    currentSectionKey={currentSectionKey}
                    isAuthor={isAuthor}
                    readOnly={readOnly}
                    isMobile={isMobile}
                    chapterSectionPublishBusyKey={chapterSectionPublishBusyKey}
                    onHeadingNavigation={handleHeadingNavigation}
                    onChapterSectionPublish={handleChapterSectionPublish}
                    entryByKey={entryByKey}
                    tagRegistry={tagRegistry}
                    tagColorMap={tagColorMap}
                    onSectionDragEnd={handleSectionDragEnd}
                    onFlatSectionsOrderChange={persistFlatSectionsOrder}
                    sectionDependencies={sectionDependencies}
                    onEditDependency={(dep) =>
                      setAddDepDialog({
                        initialFromKey: dep.fromKey,
                        initialToKey: dep.toKey,
                        initialReason: dep.reason,
                        initialKind: dep.kind,
                      })
                    }
                    onRemoveDependency={removeSectionDependency}
                    onCreateDependency={(fromKey, toKey) =>
                      setAddDepDialog({ initialFromKey: fromKey, initialToKey: toKey, initialReason: '' })
                    }
                    onAddChapterBefore={addChapterGroupBefore}
                    onRenameChapter={renameChapterGroup}
                    onSetChapterNote={setChapterGroupNote}
                    onDeleteChapter={deleteChapterGroup}
                    onSectionTagsPersisted={refetchChapterSectionsFromServer}
                    onRegistrySaved={applyTagRegistry}
                    elementSectionOrder={book?.settings?.elementSectionOrder}
                    onElementSectionOrderChange={persistElementSectionOrder}
                    onSectionContentSaved={handleSectionContentSaved}
                    onOpenContinuousView={() => {
                      setContinuousView(true)
                      setSidebarOpen(false)
                    }}
                  />
                )
          }
          renderChapterList={() => {
            const chapterListProps = {
              onSelect: handleSidebarChapterSelect,
              onDelete: handleDeleteChapter,
              onRename: handleRenameChapter,
              onTag: handleOpenTagChapter,
              onPublish: handleChapterPublish,
              getHeadings: getChapterHeadings,
              onHeadingClick: handleHeadingNavigation,
            }

            // Omit materialized chapter-export files from the source-file list (see docs/chapter-sections.md).
            const items = [...chapters].sort((a, b) => a.order - b.order)

            const listContent = items.map((chapter) => (
              <SortableChapterItem
                key={chapter.filename}
                chapter={chapter}
                isSelected={currentChapter?.filename === chapter.filename}
                isLoadingSelection={tocChapterLoadingFilename === chapter.filename}
                isExpanded={expandedChapterFilename === chapter.filename}
                book={book}
                {...chapterListProps}
                showPublish={false}
                activeHeadingId={activeHeadingId}
                tagMatchColor={
                  tagMatchedChapters.has(chapter.filename) && selectedTagIds.length > 0
                    ? resolveTagColor(selectedTagIds[0], tagColorMap, tagRegistry)
                    : null
                }
                tagColorMap={tagColorMap}
                tagRegistry={tagRegistry}
                suppressAutoScroll={suppressSidebarAutoScroll}
              />
            ))

            if (isMobile) {
              return listContent
            }

            return (
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={items.map((ch) => ch.filename)} strategy={verticalListSortingStrategy}>
                  {listContent}
                </SortableContext>
              </DndContext>
            )
          }}
        />

        {/* Document Area (middle column) */}
        <EditorDocumentPane
          currentChapter={currentChapter}
          chaptersCount={chapters.length}
          onCreateChapter={handleCreateChapter}
          forceContent={continuousView}
          topSlot={
            <>
              {continuousView ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid var(--toolbar-page-border)',
                    bgcolor: 'var(--bg-surface-subtle)',
                  }}
                >
                  <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {book?.metadata?.direction === 'rtl' ? 'עריכה רציפה' : 'Continuous editing'}
                    <Typography component="span" sx={{ ml: 1, color: 'var(--text-secondary)', fontWeight: 400 }}>
                      {placedSectionEntries.length}
                    </Typography>
                  </Typography>
                  <Button size="small" variant="outlined" onClick={() => setContinuousView(false)}>
                    {book?.metadata?.direction === 'rtl' ? 'חזרה לעורך' : 'Back to editor'}
                  </Button>
                </Box>
              ) : tabsEnabled && tabs.length > 0 ? (
                <EditorTabBar
                  tabs={tabs}
                  activeFilename={currentChapter?.filename ?? chapterId ?? null}
                  chapters={chapters}
                  hasUnsavedChanges={hasUnsavedChanges}
                  onSelect={handleTabBarSelect}
                  onClose={handleTabBarClose}
                  onReorder={reorderTabs}
                  direction={book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'}
                  titleFontFamily={book?.settings.titleFontFamily}
                />
              ) : null}
              {!continuousView && isAuthor && !readOnly && bookId && currentChapter && (
                <ContentProposalBulkActions
                  key={`${bookId}:${currentChapter.filename}`}
                  bookId={bookId}
                  filename={currentChapter.filename}
                  proposals={fileProposals}
                  onApply={handleApplyProposal}
                />
              )}
            </>
          }
        >
          {continuousView && bookId ? (
            <ContinuousPlacedSectionsView
              bookId={bookId}
              book={book}
              placedEntries={placedSectionEntries}
              onSectionContentSaved={handleSectionContentSaved}
            />
          ) : (
            currentChapter && (
              <Box
                onScroll={(e: React.UIEvent<HTMLDivElement>) => {
                  const filename = currentChapterRef.current?.filename
                  if (filename) {
                    tabScrollTopsRef.current.set(filename, e.currentTarget.scrollTop)
                  }
                }}
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  overflow: 'auto',
                  py: { xs: 0, md: 2 },
                  direction: book?.metadata?.direction === 'rtl' ? 'ltr' : undefined,
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    border: { xs: 'none', md: '1px solid var(--toolbar-page-border)' },
                    borderRadius: 0,
                    maxWidth: '816px', // 8.5 inches at 96 DPI like Google Docs
                    width: '100%',
                    minHeight: 'calc(100% - 16px)',
                    display: 'flex',
                    flexDirection: 'column',
                    direction: book?.metadata?.direction === 'rtl' ? 'rtl' : undefined,
                    background: PAPER_BACKGROUND,
                    '&::after': PAPER_TEXTURE_OVERLAY,
                    filter: chapterTransitioning ? 'blur(1.5px) saturate(0.88)' : 'none',
                    opacity: chapterTransitioning ? 0.72 : 1,
                    transition: 'filter 0.18s ease, opacity 0.18s ease',
                  }}
                >
                  {isAuthor && currentChapter && tagRegistryLoaded && (currentChapter.fileTags || []).length > 0 && (
                    <Box
                      sx={{
                        px: { xs: 2, md: 3 },
                        py: { xs: 1.5, md: 2 },
                        borderBottom: '1px solid var(--toolbar-page-border)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {(currentChapter.fileTags || []).map((tag) => {
                          const c = resolveTagColor(tag, tagColorMap, tagRegistry)
                          return (
                            <Box
                              key={tag}
                              component="span"
                              sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.6,
                                px: 1,
                                py: 0.55,
                                borderRadius: 1.5,
                                border: `1px solid ${c}66`,
                                backgroundColor: `${c}1a`,
                                color: '#fff',
                                fontSize: '0.8rem',
                                fontWeight: 600,
                                lineHeight: 1.2,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: c, flex: '0 0 auto' }}
                              />
                              {getTagDisplayName(tagRegistry, tag)}
                            </Box>
                          )
                        })}
                      </Box>
                    </Box>
                  )}

                  {proposalError && <Alert severity="warning">לא ניתן לטעון הצעות: {proposalError}</Alert>}
                  <LexicalEditor
                    proposalReview={{ proposals: fileProposals, onAction: handleInlineProposalAction }}
                    key={`${currentChapter?.filename}-${externalContentEpoch}`}
                    ref={editorRef}
                    initialContent={getEditorContent() || undefined}
                    undoRedoManager={undoRedoManager}
                    onChange={handleEditorContentChange}
                    onSave={handleSave}
                    saving={saving}
                    autoSaving={autoSaving}
                    hasUnsavedChanges={hasUnsavedChanges}
                    recentlySaved={showSavedIndicator}
                    placeholder={`Start writing "${currentChapter.title}"...`}
                    readOnly={readOnly}
                    readerView={false}
                    showToolbar={isAuthor}
                    showSaveButton={false}
                    currentDirection={getBookMetadata().bookDir}
                    onToggleDirection={handleToggleDirection}
                    onSetDirection={handleSetDirection}
                    debugMode={debugMode}
                    onToggleDebug={handleToggleDebug}
                    showLineNumbers={showLineNumbers}
                    onToggleLineNumbers={handleToggleLineNumbers}
                    onEncryptedClick={handleEncryptedClick}
                    onEncryptedContextMenu={handleEncryptedContextMenu}
                    mnemonicMap={mnemonicMap}
                    isAuthor={isAuthor}
                    bookId={bookId}
                    chapterId={chapterId}
                    onOpenEmbedSource={handleOpenEmbedSource}
                    annotations={currentChapter?.annotations || []}
                    onAnnotationChange={handleAnnotationChange}
                    onAnnotationsBulkUpdate={handleAnnotationsBulkUpdate}
                    onAskAssistant={handleAskAssistant}
                    onAnnotationsRepaired={handleAnnotationsRepaired}
                    toolbarContainerRef={toolbarContainerRef}
                    bookSettings={book?.settings}
                    tagColorMap={tagColorMap}
                    tagRegistry={tagRegistry}
                    onTagRegistrySaved={applyTagRegistry}
                    initialScrollBlockId={pendingScrollRef.current}
                    initialScrollHighlightText={pendingHighlightRef.current}
                    initialScrollHighlightOffset={pendingHighlightOffsetRef.current}
                    initialScrollTop={pendingScrollTopRef.current}
                    initialSelection={pendingSelectionRef.current}
                    onAddDependency={handleAddDependencyFromEditor}
                    style={{
                      minHeight: '600px',
                    }}
                  />
                  <LineNumberGutter
                    ast={currentContent}
                    direction={getBookMetadata().bookDir}
                    visible={showLineNumbers && isAuthor && !readOnly}
                    onGoToBlock={(blockId) => editorRef.current?.scrollToBlock(blockId)}
                  />
                  {readOnly && !isAuthor && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 5,
                        cursor: 'not-allowed',
                      }}
                    />
                  )}
                  {searchChapterTransitioning && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        pointerEvents: 'none',
                        bgcolor: 'rgba(0, 0, 0, 0.12)',
                        backdropFilter: 'blur(1px)',
                      }}
                    >
                      <Box
                        sx={{
                          px: 2,
                          py: 1,
                          borderRadius: 999,
                          bgcolor: 'rgba(0, 0, 0, 0.38)',
                          color: '#fff',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                        }}
                      >
                        Opening result...
                      </Box>
                    </Box>
                  )}
                  {/* Publishing Modal */}
                  {publishTarget && (
                    <PublishingModal
                      open={publishOpen}
                      onClose={() => {
                        setPublishOpen(false)
                        setPublishTarget(null)
                      }}
                      bookId={bookId!}
                      target={publishTarget}
                      onPublishSuccess={async () => {
                        const refreshFilename =
                          publishTarget.kind === 'whole' ? publishTarget.filename : publishTarget.sourceFilename
                        setPublishOpen(false)
                        setPublishTarget(null)
                        await refreshChapterState(refreshFilename)
                      }}
                    />
                  )}
                </Box>
              </Box>
            )
          )}
        </EditorDocumentPane>

        {/* Side Pane: unified search + tag filter (left side in RTL, rendered last in flex order) */}
        {isAuthor && !isMobile && bookId && (
          <SidePane
            ref={sidePaneRef}
            bookId={bookId}
            onApplyProposal={handleApplyProposal}
            assistantAvailable={assistantAvailable}
            assistantFilename={currentChapter?.filename}
            assistantStartBlockId={assistant.startBlockId}
            assistantMarkedText={assistant.markedText}
            onGoTo={handleGoTo}
            onSearchNavigate={handleSearchResultOpen}
            onSearchPreview={handleDesktopPreviewSelection}
            onSearchStepSelection={handleStepSidePaneSearchSelection}
            onSearchEnterSelection={handleEnterSearchSelection}
            selectedSearchResultKey={selectedPreviewResult ? getSearchResultKey(selectedPreviewResult) : null}
            searchQuery={searchQuery}
            searchResults={searchResults}
            searchLoading={searchLoading}
            searchSearched={searchSearched}
            direction={book?.metadata?.direction || 'ltr'}
            onSearchQueryChange={setSearchQuery}
            onSearchClear={handleSearchClear}
            isPreviewOpen={keyboardPreviewOpen && Boolean(selectedPreviewResult)}
            onClosePreview={handleSearchPreviewClose}
            showPreviewPopup={keyboardPreviewOpen && Boolean(selectedPreviewResult)}
            previewPopupPlacement={book?.metadata?.direction === 'rtl' ? 'right-start' : 'left-start'}
            previewPopupContent={
              <Box
                onKeyDown={(event) => {
                  if (event.key === 'Escape') {
                    event.stopPropagation()
                    handleSearchPreviewClose()
                  }
                }}
                sx={{
                  width: 'min(72vw, 920px)',
                  height: 'min(78vh, 760px)',
                  display: 'flex',
                  overflow: 'hidden',
                  bgcolor: 'var(--bg-surface)',
                  border: '2px solid var(--border-primary)',
                  borderBottomWidth: '4px',
                  outline: '1px solid var(--floating-panel-ring)',
                  outlineOffset: '-3px',
                  borderRadius: 1.5,
                  backgroundImage: 'linear-gradient(180deg, rgba(255,255,255,0.02), rgba(255,255,255,0))',
                  boxShadow: 'var(--floating-panel-shadow)',
                  transform: `translate(${previewPopupOffset.x}px, ${previewPopupOffset.y}px)`,
                  willChange: previewPopupDragging ? 'transform' : 'auto',
                }}
              >
                <SearchPreviewPanel
                  sourceFilename={selectedPreviewResult?.filename}
                  onOpenEmbedSource={handleOpenPreviewEmbedSource}
                  previewIdentity={selectedPreviewResult ? `content:${selectedPreviewResult.filename}` : null}
                  previewTitle={selectedPreviewResult?.chapterTitle || null}
                  previewAst={resolvedPreviewAst}
                  previewLoading={resolvedPreviewLoading}
                  previewError={resolvedPreviewError}
                  previewBlockId={selectedPreviewResult?.blockId || null}
                  previewHighlightText={
                    selectedPreviewResult && selectedPreviewResult.matchStart < selectedPreviewResult.matchEnd
                      ? selectedPreviewResult.snippet.slice(
                          selectedPreviewResult.matchStart,
                          selectedPreviewResult.matchEnd,
                        )
                      : null
                  }
                  previewHighlightOffset={
                    selectedPreviewResult && selectedPreviewResult.blockMatchStart > 0
                      ? selectedPreviewResult.blockMatchStart
                      : null
                  }
                  book={book}
                  onHeaderPointerDown={handlePreviewPopupPointerDown}
                  headerDraggable={true}
                  headerDragging={previewPopupDragging}
                  editable={!resolvedPreviewError && !resolvedPreviewLoading && Boolean(resolvedPreviewAst)}
                  onChange={handlePreviewEditChange}
                  onSave={handlePreviewEditSave}
                  editorRef={searchPreviewEditorRef}
                  saving={previewSaving}
                  hasUnsavedChanges={previewDirty}
                  recentlySaved={previewRecentlySaved}
                />
              </Box>
            }
            selectedTagIds={selectedTagIds}
            onTagsChange={handleTagsChange}
            tags={tagRegistry}
            tagColorMap={tagColorMap}
            tagUsageCounts={tagUsageFlatCounts}
            onOpenTagManager={() => setTagManagerOpen(true)}
          />
        )}
      </Box>

      {isAuthor && isMobile && bookId && (
        <MobileSearchDialog
          open={mobileSidePaneOpen}
          onClose={() => {
            setMobilePreviewOpen(false)
            setMobileSidePaneOpen(false)
          }}
          query={searchQuery}
          results={searchResults}
          loading={searchLoading}
          searched={searchSearched}
          direction={book?.metadata?.direction || 'ltr'}
          selectedResultKey={selectedPreviewResult ? getSearchResultKey(selectedPreviewResult) : null}
          selectedPreviewResult={selectedPreviewResult}
          previewOpen={mobilePreviewOpen}
          previewHeightRatio={mobilePreviewHeightRatio}
          previewDragging={mobilePreviewDragging}
          previewAst={resolvedPreviewAst}
          previewLoading={resolvedPreviewLoading}
          previewError={resolvedPreviewError}
          book={book}
          searchRef={mobileSearchRef}
          onQueryChange={setSearchQuery}
          onClear={handleSearchClear}
          onNavigate={handleSearchNavigate}
          onPreviewSelect={handleMobilePreviewSelection}
          onEnterSelection={handleEnterSearchSelection}
          onClosePreview={() => setMobilePreviewOpen(false)}
          onPreviewHeaderPointerDown={handleMobilePreviewPointerDown}
          selectedTagIds={selectedTagIds}
          onTagsChange={handleTagsChange}
          tags={tagRegistry}
          tagColorMap={tagColorMap}
          tagUsageCounts={tagUsageFlatCounts}
          onOpenTagManager={() => {
            setMobileSidePaneOpen(false)
            setTagManagerOpen(true)
          }}
        />
      )}

      {/* Decryption Modal (for readers) */}
      <DecryptionModal
        open={decryptionModalOpen}
        onClose={() => setDecryptionModalOpen(false)}
        encryptedContent={encryptedContentToDecrypt}
        onDecryptSuccess={handleDecryptSuccess}
        direction={book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'}
        titleFontFamily={book?.settings?.titleFontFamily}
      />

      {/* Context Menu (for authors) */}
      <EncryptedContextMenu
        anchorPosition={contextMenuPosition}
        onClose={() => setContextMenuPosition(null)}
        onRemoveEncryption={handleRemoveEncryption}
        onEditLockPhrase={
          bookId && !contextMenuKind.character && !contextMenuKind.authorHeld
            ? () => setLockPhraseTarget(contextMenuEncryptionId)
            : undefined
        }
        onReleaseToEveryone={
          bookId && contextMenuKind.authorHeld ? () => setReleaseTarget(contextMenuEncryptionId) : undefined
        }
      />

      <ReleasePassageDialog
        open={Boolean(releaseTarget)}
        onClose={() => setReleaseTarget(null)}
        onConfirm={async () => {
          if (!bookId || !releaseTarget || !currentChapter?.filename) {
            return
          }
          try {
            await apiService.setCharacterConsent(bookId, currentChapter.filename, releaseTarget, 'release')
            toast.success('הקטע נפתח לכולם')
            await refreshChapterState(currentChapter.filename)
          } catch (err: any) {
            toast.error(err?.message || 'לא הצלחנו לפתוח את הקטע')
          } finally {
            setReleaseTarget(null)
          }
        }}
      />

      {bookId && lockPhraseTarget && (
        <LockPhraseDialog
          open
          bookId={bookId}
          encryptionId={lockPhraseTarget}
          onClose={() => setLockPhraseTarget(null)}
        />
      )}

      {/* Book Settings Modal (for authors) */}
      {book && (
        <BookSettingsModal
          open={bookSettingsOpen}
          onClose={() => setBookSettingsOpen(false)}
          book={book}
          onBookUpdated={handleBookUpdated}
        />
      )}

      {/* Tag Manager Dialog (for authors) */}
      {bookId && isAuthor && (
        <TagManagerDialog
          open={tagManagerOpen}
          onClose={() => {
            setTagManagerOpen(false)
            fetchTagData()
          }}
          bookId={bookId}
          direction={book?.metadata?.direction}
          onRegistrySaved={applyTagRegistry}
        />
      )}

      {bookId && isAuthor && tagTargetChapter && (
        <TagManagerDialog
          open={Boolean(tagTargetChapter)}
          onClose={() => setTagTargetChapter(null)}
          bookId={bookId}
          direction={book?.metadata?.direction}
          mode="select"
          selectionMode="multiple"
          initialSelectedTags={tagTargetChapter.fileTags || []}
          onSelectManyAndSave={handleSaveChapterFileTags}
          onRegistrySaved={applyTagRegistry}
        />
      )}

      {isAuthor && addDepDialog && (
        <AddDependencyDialog
          open
          bookDirection={book?.metadata?.direction || 'ltr'}
          bookFontFamily={book?.settings?.titleFontFamily}
          sections={depSectionOptions}
          existingDeps={sectionDependencies.deps}
          orderedKeys={sectionDependencies.orderedKeys}
          initialFromKey={addDepDialog.initialFromKey}
          initialToKey={addDepDialog.initialToKey}
          initialReason={addDepDialog.initialReason}
          initialKind={addDepDialog.initialKind}
          onSave={async (fromKey, toKey, reason, kind) => {
            await upsertSectionDependency(fromKey, toKey, reason, kind)
            setAddDepDialog(null)
          }}
          onDelete={async (depId) => {
            await removeSectionDependency(depId)
            setAddDepDialog(null)
          }}
          onCancel={() => setAddDepDialog(null)}
        />
      )}

      {bookId && isMobile && (
        <AssistantPane
          onApplyProposal={handleApplyProposal}
          open={assistant.open}
          onClose={() => setAssistant({ open: false })}
          bookId={bookId}
          filename={currentChapter?.filename}
          startBlockId={assistant.startBlockId}
          markedText={assistant.markedText}
          direction={book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'}
          onGoTo={handleGoTo}
        />
      )}

      <FlaggedSpotsDialog
        open={flaggedSpots.open}
        title={flaggedSpots.title}
        items={flaggedSpots.items}
        direction={book?.metadata?.direction || 'ltr'}
        fullScreen={isMobile}
        onPick={flaggedSpots.onPick}
        onClose={flaggedSpots.onClose}
        onGoToJournal={flaggedSpots.onGoToJournal}
      />

      <TabSwitcher
        enabled={tabsEnabled}
        tabs={tabs}
        chapters={chapters}
        getMruOrder={getMruFilenames}
        direction={book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'}
        titleFontFamily={book?.settings.titleFontFamily}
        onSwitch={handleTabBarSelect}
      />

      {bookId && historyAvailable && (
        <HistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          bookId={bookId}
          direction={book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'}
          bookFontFamily={book?.settings.defaultFontFamily}
        />
      )}

      <KeyboardShortcutsDialog enabled={isAuthor} direction={book?.metadata?.direction === 'rtl' ? 'rtl' : 'ltr'} />
    </Box>
  )
}

export default BookEditor
